import type { LayoutProfile, PresenterLayout, ResolvedTheme } from '../types';

type Clip = 'idle' | 'win' | 'loss';
const FADE_MS = 140;

/**
 * WebKit mascot (Safari, every iOS browser): the clips play in real <video> elements (HEVC with alpha) that the
 * browser composites under the transparent game canvas — no WebGL upload at all. Measured in the iOS Simulator
 * (`public/diag.html`, showcase `?debug=1&probe=1`): WebKit's WebGL uploads of HEVC-alpha frames are either opaque
 * (texImage2D), stall the main thread for up to a second (texSubImage2D) or stay frozen (canvas copies), while a
 * plain <video> plays smoothly with correct transparency.
 * Behaviour mirrors MascotLayer: contain-fit into `layout.<profile>.mascot` anchored bottom-centre, the poster
 * shows until the idle clip really plays, reactions play once at t0 and cross-fade back, a spin returns to idle.
 * The theme background moves to a DOM layer beneath the videos (DomBackdrop); everything else stays in the canvas.
 *
 * Real-device rules (iOS): muted inline play() may still be refused (Low Power Mode, per-site auto-play setting)
 * until a user gesture — the first touch/click anywhere retries; WebKit pauses videos when the page is hidden and
 * does not resume them — visibility changes and stray pauses restart the idle loop; the idle element is kept fully
 * opaque under the poster (a transparent video is easy to treat as "not visible"); a reaction is shown only once
 * its own `playing` event fires, so a refused reaction never blanks the character.
 */
export class DomMascot {
  readonly el: HTMLDivElement;
  private readonly poster: HTMLImageElement | null = null;
  readonly videos: Partial<Record<Clip, HTMLVideoElement>> = {};
  private box: LayoutProfile['mascot'] | null = null;
  private shown = false;
  live = false;
  private reaction = 0;
  private disposed = false;
  private readonly onGesture = () => {
    if (!this.live) this.startIdle();
  };
  private readonly onVisibility = () => {
    if (!document.hidden && this.shown) this.startIdle();
  };
  /** Last events (bounded), readable from the debug handle for device diagnostics. */
  readonly trace: string[] = [];
  /** Frames the browser reported as presented for the idle clip (requestVideoFrameCallback), for diagnostics. */
  presented = 0;

  constructor(o: { container: HTMLElement; before: Node | null; clips: { idle?: string; win?: string; loss?: string }; poster?: string }) {
    this.el = document.createElement('div');
    this.el.className = 'slot-mascot';
    Object.assign(this.el.style, { position: 'absolute', left: '0', top: '0', width: '0', height: '0', display: 'none', pointerEvents: 'none' });
    o.container.insertBefore(this.el, o.before);
    // Idle sits at the bottom fully opaque, reactions above it hidden, the poster on top until the idle clip plays.
    if (o.clips.idle) this.make('idle', o.clips.idle, true);
    if (o.clips.win) this.make('win', o.clips.win, false);
    if (o.clips.loss) this.make('loss', o.clips.loss, false);
    if (o.poster) {
      const img = document.createElement('img');
      img.decoding = 'async';
      img.alt = '';
      img.draggable = false;
      this.fill(img, 1);
      img.src = o.poster;
      this.el.appendChild(img);
      this.poster = img;
    }
    for (const ev of ['pointerdown', 'touchstart', 'keydown'] as const) document.addEventListener(ev, this.onGesture, { capture: true, passive: true });
    document.addEventListener('visibilitychange', this.onVisibility);
  }

  get available(): boolean {
    return !!this.videos.idle;
  }

  private mark(event: string): void {
    this.trace.push(`${Math.round(performance.now())} ${event}`);
    if (this.trace.length > 40) this.trace.shift();
  }

  private fill(node: HTMLElement, opacity: number): void {
    Object.assign(node.style, {
      position: 'absolute', inset: '0', width: '100%', height: '100%', objectFit: 'contain', objectPosition: '50% 100%',
      opacity: String(opacity), transition: `opacity ${FADE_MS}ms linear`, pointerEvents: 'none',
    });
  }

  private make(clip: Clip, src: string, loop: boolean): void {
    const v = document.createElement('video');
    v.muted = true;
    v.defaultMuted = true;
    v.loop = loop;
    v.playsInline = true;
    v.preload = 'auto';
    v.disablePictureInPicture = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('muted', '');
    v.setAttribute('aria-hidden', 'true');
    v.setAttribute('disableremoteplayback', '');
    this.fill(v, loop ? 1 : 0);
    v.src = src;
    this.videos[clip] = v;
    this.el.appendChild(v);
    v.addEventListener('playing', () => {
      this.mark(`${clip} playing t=${v.currentTime.toFixed(2)}`);
      if (clip === 'idle') this.goLive();
      else if (this.pendingReaction === clip) {
        this.pendingReaction = null;
        this.show(clip);
      }
    });
    v.addEventListener('pause', () => {
      this.mark(`${clip} pause t=${v.currentTime.toFixed(2)}`);
      // WebKit pauses media it decides is not visible or when the app goes to background; bring idle back.
      if (loop && this.shown && !this.disposed && !v.ended) setTimeout(() => {
        if (this.shown && !this.disposed && v.paused) this.kick(v, clip);
      }, 400);
    });
    v.addEventListener('loadeddata', () => {
      this.mark(`${clip} loadeddata ${v.videoWidth}x${v.videoHeight}`);
      // Reaction clips: iOS only decodes on a playback request, so they are kicked and parked on their first frame.
      if (!loop) {
        v.pause();
        try {
          v.currentTime = 0;
        } catch {
          /* not seekable yet */
        }
      }
    });
    v.addEventListener('error', () => this.mark(`${clip} error ${v.error?.code ?? ''}`));
    const rv = v as HTMLVideoElement & { requestVideoFrameCallback?: (cb: () => void) => number };
    if (clip === 'idle' && typeof rv.requestVideoFrameCallback === 'function') {
      const onFrame = () => {
        if (this.disposed) return;
        this.presented++;
        rv.requestVideoFrameCallback!(onFrame);
      };
      rv.requestVideoFrameCallback(onFrame);
    }
    this.kick(v, clip);
  }

  private kick(v: HTMLVideoElement, clip: Clip): void {
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch((e: unknown) => this.mark(`${clip} play rejected ${(e as Error)?.name ?? ''}`));
  }

  /** Reaction clip waiting for its first presented frame before it is shown. */
  private pendingReaction: Clip | null = null;

  /** Which element is visible: a clip, or the poster (null). Idle stays opaque underneath everything. */
  private show(clip: Clip | null): void {
    for (const [k, v] of Object.entries(this.videos)) if (v) v.style.opacity = k === clip || k === 'idle' ? '1' : '0';
    if (this.poster) this.poster.style.opacity = clip === null ? '1' : '0';
  }

  private goLive(): void {
    const v = this.videos.idle;
    if (this.live || this.disposed || !this.shown || !v || v.paused) return;
    this.live = true;
    this.mark('live');
    this.show('idle');
  }

  private startIdle(): void {
    const v = this.videos.idle;
    if (!v || !this.shown) return;
    const p = v.play();
    if (p && typeof p.then === 'function') p.then(() => this.goLive(), () => undefined);
    else this.goLive();
  }

  /** Place the box in CSS px from the presenter's design→CSS mapping; hidden when the profile has no box. */
  layout(profile: LayoutProfile, info: PresenterLayout | null): void {
    this.box = profile.mascot ?? null;
    if (!this.box || !info) {
      this.el.style.display = 'none';
      if (this.shown) {
        this.shown = false;
        this.mark('hidden (no box)');
        for (const v of Object.values(this.videos)) v?.pause();
      }
      return;
    }
    const b = this.box;
    Object.assign(this.el.style, {
      display: 'block',
      left: `${info.x + b.x * info.scale}px`,
      top: `${info.y + b.y * info.scale}px`,
      width: `${b.width * info.scale}px`,
      height: `${b.height * info.scale}px`,
    });
    if (!this.shown) {
      this.shown = true;
      this.mark(`shown box=${b.x},${b.y},${b.width}x${b.height} scale=${info.scale.toFixed(3)}`);
      this.startIdle();
    }
  }

  /** Back to the idle loop, interrupting any reaction (called when a spin starts). */
  idle(): void {
    this.reaction++;
    this.pendingReaction = null;
    if (!this.videos.idle) return;
    this.startIdle();
    if (this.live) this.show('idle');
    for (const k of ['win', 'loss'] as const) {
      const v = this.videos[k];
      if (!v) continue;
      setTimeout(() => {
        if (this.disposed || v.style.opacity === '1') return;
        v.pause();
      }, FADE_MS + 40);
    }
  }

  /** One cycle of a reaction clip at t0, then idle. Silently ignored when the clip is missing or not ready. */
  react(kind: 'win' | 'loss'): void {
    const v = this.videos[kind];
    this.mark(`react ${kind}${!v ? ' (no clip)' : !this.live ? ' (not live)' : v.readyState < 2 ? ' (not ready)' : ''}`);
    if (!v || !this.shown || !this.live || v.readyState < 2) return;
    const id = ++this.reaction;
    try {
      v.currentTime = 0;
    } catch {
      /* not seekable yet */
    }
    // Shown on its own `playing` event (see make): a refused play() leaves the idle loop untouched.
    this.pendingReaction = kind;
    this.kick(v, kind);
    const done = () => {
      v.removeEventListener('ended', done);
      if (id !== this.reaction || this.disposed) return;
      this.idle();
    };
    v.addEventListener('ended', done);
    const ms = Number.isFinite(v.duration) && v.duration > 0 ? v.duration * 1000 + 250 : 4000;
    setTimeout(() => {
      if (id === this.reaction && !this.disposed && v.style.opacity === '1') done();
    }, ms);
  }

  destroy(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.reaction++;
    for (const ev of ['pointerdown', 'touchstart', 'keydown'] as const) document.removeEventListener(ev, this.onGesture, { capture: true });
    document.removeEventListener('visibilitychange', this.onVisibility);
    for (const v of Object.values(this.videos)) {
      if (!v) continue;
      v.pause();
      v.removeAttribute('src');
      v.load();
    }
    this.el.remove();
  }
}

/** Theme background as a DOM layer (cover, centred — same fit as the canvas sprite) beneath the DOM mascot. */
export class DomBackdrop {
  readonly el: HTMLDivElement;
  private readonly base: HTMLDivElement;
  private readonly free: HTMLDivElement;

  constructor(o: { container: HTMLElement; before: Node | null; theme: ResolvedTheme; free: boolean }) {
    this.el = document.createElement('div');
    this.el.className = 'slot-backdrop';
    Object.assign(this.el.style, { position: 'absolute', inset: '0', pointerEvents: 'none', overflow: 'hidden' });
    this.base = this.layer(o.theme.assets.background?.base, o.theme.palette.background);
    this.free = this.layer(o.theme.assets.background?.freeSpins, o.theme.palette.backgroundFreeSpins);
    this.free.style.opacity = o.free ? '1' : '0';
    this.el.append(this.base, this.free);
    o.container.insertBefore(this.el, o.before);
  }

  private layer(url: string | undefined, color: string): HTMLDivElement {
    const d = document.createElement('div');
    Object.assign(d.style, { position: 'absolute', inset: '0', backgroundColor: color, backgroundPosition: 'center', backgroundSize: 'cover', backgroundRepeat: 'no-repeat', transition: 'opacity 600ms linear' });
    if (url) d.style.backgroundImage = `url("${url}")`;
    return d;
  }

  setFree(on: boolean, animate: boolean): void {
    this.free.style.transition = animate ? 'opacity 600ms linear' : 'none';
    this.free.style.opacity = on ? '1' : '0';
  }

  destroy(): void {
    this.el.remove();
  }
}
